import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Image as ImageIcon, X } from "lucide-react";
import { fetchOrderChangeImageBlob, fetchOrderChangeNotes } from "../../api/OrderChangeNoteAPIs";

const OrderChangeImagesButton = ({ orderId, count = 0, variant = "badge", always = false }) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notes, setNotes] = useState([]);
  const [preview, setPreview] = useState(null);
  const imageCount = Number(count) || 0;
  const visible = always || imageCount > 0;

  const load = useCallback(async () => {
    if (!orderId) return;
    setLoading(true);
    setError("");
    try {
      const data = await fetchOrderChangeNotes(orderId);
      const withUrls = await Promise.all(
        (data.notes || []).map(async (note) => ({
          ...note,
          images: await Promise.all(
            (note.images || []).map(async (image) => {
              try {
                const blob = await fetchOrderChangeImageBlob(orderId, image.id);
                return { ...image, url: URL.createObjectURL(blob) };
              } catch {
                return { ...image, url: "", missing: true };
              }
            })
          ),
        }))
      );
      setNotes(withUrls);
    } catch (err) {
      setError(err.message || "تعذر تحميل الصور المرجعية");
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    if (!open) return undefined;
    load();
    return () => {
      setNotes((current) => {
        current.forEach((note) => note.images?.forEach((image) => image.url && URL.revokeObjectURL(image.url)));
        return [];
      });
    };
  }, [open, load]);

  if (!visible || !orderId) return null;

  const openViewer = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen(true);
  };

  const buttonClass =
    variant === "menu"
      ? "w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm font-medium text-slate-700 hover:bg-purple-50 hover:text-purple-800 transition text-right"
      : "inline-flex items-center gap-1.5 mt-2 px-2.5 py-1 rounded-full bg-purple-600 text-white text-xs font-bold shadow-sm";

  const dialog = open
    ? createPortal(
        <div className="fixed inset-0 z-[90] bg-black/60 flex items-end sm:items-center justify-center p-3" dir="rtl" onClick={() => setOpen(false)}>
          <div className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-2xl shadow-2xl" onClick={(event) => event.stopPropagation()}>
            <div className="sticky top-0 bg-white border-b px-4 py-3 flex items-center justify-between">
              <div>
                <p className="text-lg font-bold text-slate-900">صور مرجعية للتغييرات</p>
                <p className="text-sm text-slate-500">طلب #{orderId} — هذه الصور للطباعة وليست صور المنتج</p>
              </div>
              <button type="button" onClick={() => setOpen(false)} className="p-2 rounded-full hover:bg-slate-100" aria-label="إغلاق">
                <X size={18} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {loading && <p className="text-slate-500">جاري تحميل الصور...</p>}
              {error && <p className="text-red-600">{error}</p>}
              {!loading && !error && notes.every((note) => (note.images || []).length === 0) && (
                <p className="text-slate-500">لا توجد صور مرجعية لهذا الطلب.</p>
              )}
              {notes.map((note) => (
                <section key={note.id} className="border border-purple-100 rounded-2xl p-3 bg-purple-50/40">
                  <p className="text-sm font-bold text-purple-900 mb-2 whitespace-pre-wrap">{note.text || "بدون وصف"}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {(note.images || []).map((image) => (
                      <button
                        key={image.id}
                        type="button"
                        className="aspect-square rounded-xl overflow-hidden bg-white border"
                        onClick={() => image.url && setPreview(image)}
                      >
                        {image.url ? (
                          <img src={image.url} alt={image.originalFileName || "صورة مرجعية"} className="w-full h-full object-cover" />
                        ) : (
                          <span className="text-xs text-slate-400 p-2">الصورة غير متاحة</span>
                        )}
                      </button>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </div>
          {preview?.url && (
            <div className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4" onClick={() => setPreview(null)}>
              <img src={preview.url} alt="معاينة الصورة" className="max-w-full max-h-[85vh] rounded-xl" />
            </div>
          )}
        </div>,
        document.body
      )
    : null;

  return (
    <>
      <button type="button" className={buttonClass} onClick={openViewer}>
        <ImageIcon size={variant === "menu" ? 15 : 13} />
        صور مرجعية{imageCount > 0 ? ` (${imageCount})` : ""}
      </button>
      {dialog}
    </>
  );
};

export default OrderChangeImagesButton;
